/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
    updatedQuantity?: number;
}

export interface StyleProps {
    wrapper?: InjectableCss;
}

export const productQuantityWrapperStyles: StyleProps = {
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: flex-start;
            gap: 16px;
        `,
    },
};

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        currencyCode: state.context.session.currency?.currencyCode ?? "",
        onQtyOrderedChanged: props.productContext.onQtyOrderedChanged
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const wrapperStyles = productQuantityWrapperStyles;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-inner-spin-button,
            input[type="number"]::-webkit-outer-spin-button {
                opacity: 1;
            }

            background: red;
        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;

            width: 80px;
        `,
    },
};

const ProductAvailabilityQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, properties },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    currencyCode,
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    updatedQuantity,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(updatedQuantity);
    const showAddToCart = properties?.showRtbCodeAddToCart;

    const keyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();      // stop default behavior
            event.stopPropagation();     // stop bubbling
            event.currentTarget.blur();  // trigger onBlur
        }
    };


    const qtyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const possibleNumber = Number(value);

        if (!Number.isNaN(possibleNumber)) {
            setProductQty(possibleNumber);
            if (onQtyOrderedChanged) {
                onQtyOrderedChanged(possibleNumber);
            }
        }
    };

    const blurHandler = () => {
        if (!onQtyOrderedChanged) {
            logger.warn(
                `There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`
            );
            return;
        }
        if (productQty) {
            onQtyOrderedChanged(productQty);
        }
    };
    return (
        <StyledWrapper {...wrapperStyles.wrapper} className="qo-list-item">

            <TextField
                label={labelOverride ?? translate("QTY")}
                value={productQty}
                type="number"
                min={0}
                max={999999}
                data-test-selector="product_qtyOrdered"
                {...otherProps}
                onKeyDown={keyDownHandler}
                onChange={qtyChangeHandler}
                onBlur={blurHandler}
                {...styles}
            />

        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductAvailabilityQuantityOrdered));
